import React from "react";
import { USER_FACING_SPORTS, SPORTS, getSportMeta } from "../../lib/sportCategories";

/**
 * Single-select sport picker for CREATE forms (events, trips, listings).
 *
 * Renders as a responsive grid of tappable cards — each sport gets a
 * coloured card that visually "locks in" when selected. Bigger touch
 * target than a dropdown, aligns with the mobile-first nature of the
 * app, and lets users see all communities at a glance so the app feels
 * inclusive even during content creation.
 *
 * Props:
 *  - value: string | null — currently selected sport id
 *  - onChange: (sportId: string) => void
 *  - includeShared: boolean — adds the "Shared" sentinel (Hire Hub only)
 *  - required: boolean — highlights the group if unselected on submit
 */
export const SportCategoryPicker = ({
  value,
  onChange,
  includeShared = false,
  required = true,
  className = "",
}) => {
  const options = includeShared
    ? [...USER_FACING_SPORTS, SPORTS.SHARED]
    : USER_FACING_SPORTS;

  return (
    <div
      data-testid="sport-category-picker"
      className={`grid grid-cols-2 sm:grid-cols-3 gap-2 ${className}`}
      role="radiogroup"
      aria-required={required}
      aria-label="Select a sport category"
    >
      {options.map((sportId) => {
        const meta = getSportMeta(sportId);
        const active = value === sportId;
        return (
          <button
            key={sportId}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(sportId)}
            data-testid={`sport-picker-option-${sportId}`}
            className="flex items-center gap-2 rounded-xl px-3 py-3 text-sm font-medium transition-all touch-manipulation backdrop-blur-sm"
            style={{
              backgroundColor: active ? `${meta.color}33` : "rgba(255,255,255,0.04)",
              border: `1.5px solid ${active ? meta.color : "rgba(255,255,255,0.15)"}`,
              color: active ? meta.color : "inherit",
              boxShadow: active ? `0 4px 12px ${meta.color}40` : "none",
            }}
          >
            {meta.emoji && (
              <span className="text-xl" aria-hidden>
                {meta.emoji}
              </span>
            )}
            <span className="text-left leading-tight">{meta.label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default SportCategoryPicker;
